import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import RequiresAiChatToolsAlert from '@cdo/apps/aiComponentLibrary/aiChatToolsDependencyAlerts/RequiresAiChatToolsAlert';
import RedirectDialog from '@cdo/apps/code-studio/components/RedirectDialog';
import {isScriptHiddenForSection} from '@cdo/apps/code-studio/hiddenLessonRedux';
import {ViewType} from '@cdo/apps/code-studio/viewAsRedux';
import {PublishedState} from '@cdo/apps/generated/curriculum/sharedCourseConstants';
import {resourceShape} from '@cdo/apps/levelbuilder/shapes';
import {EVENTS} from '@cdo/apps/metrics/AnalyticsConstants';
import analyticsReporter from '@cdo/apps/metrics/AnalyticsReporter';
import EndOfLessonDialog from '@cdo/apps/templates/EndOfLessonDialog';
import GoogleClassroomAttributionLabel from '@cdo/apps/templates/progress/GoogleClassroomAttributionLabel';
import ProgressLegend from '@cdo/apps/templates/progress/ProgressLegend';
import ProgressTable from '@cdo/apps/templates/progress/ProgressTable';
import {unitCalendarLesson} from '@cdo/apps/templates/progress/unitCalendarLessonShapes';
import AssessmentsAnnouncementDialog from '@cdo/apps/templates/rubrics/AssessmentsAnnouncementDialog';
import {assignmentCourseVersionShape} from '@cdo/apps/templates/teacherDashboard/shapes';
import {
  onDismissRedirectDialog,
  dismissedRedirectDialog,
} from '@cdo/apps/util/dismissVersionRedirect';
import {AiChatToolsDependency} from '@cdo/generated-scripts/sharedConstants';
import i18n from '@cdo/locale';

import UnitCalendarGrid from './UnitCalendarGrid';
import UnitOverviewActionRow from './UnitOverviewActionRow';
import UnitOverviewHeader from './UnitOverviewHeader';
import UnitOverviewTopRow from './UnitOverviewTopRow';

import styles from './unit-overview.module.scss';

/**
 * Lesson progress component used in level header and script overview.
 */
class UnitOverview extends React.Component {
  static propTypes = {
    id: PropTypes.number,
    courseOfferingId: PropTypes.number,
    courseVersionId: PropTypes.number,
    courseId: PropTypes.number,
    courseTitle: PropTypes.string,
    courseLink: PropTypes.string,
    isSingleUnitCourse: PropTypes.bool,
    excludeCsfColumnInLegend: PropTypes.bool.isRequired,
    teacherResources: PropTypes.arrayOf(resourceShape),
    studentResources: PropTypes.arrayOf(resourceShape),
    showCourseUnitVersionWarning: PropTypes.bool,
    showScriptVersionWarning: PropTypes.bool,
    redirectScriptUrl: PropTypes.string,
    showRedirectWarning: PropTypes.bool,
    versions: PropTypes.objectOf(assignmentCourseVersionShape).isRequired,
    courseName: PropTypes.string,
    showAssignButton: PropTypes.bool,
    assignedSectionId: PropTypes.number,
    unitCalendarLessons: PropTypes.arrayOf(unitCalendarLesson),
    unitHasLevels: PropTypes.bool,
    weeklyInstructionalMinutes: PropTypes.number,
    showCalendar: PropTypes.bool,
    isMigrated: PropTypes.bool,
    scriptOverviewPdfUrl: PropTypes.string,
    scriptResourcesPdfUrl: PropTypes.string,
    isCsdOrCsp: PropTypes.bool,
    completedLessonNumber: PropTypes.string,
    isProfessionalLearningCourse: PropTypes.bool,
    publishedState: PropTypes.oneOf(Object.values(PublishedState)),
    participantAudience: PropTypes.string,
    showAiAssessmentsAnnouncement: PropTypes.bool,
    isOnTeacherDashboard: PropTypes.bool,
    aiChatToolsDependency: PropTypes.oneOf(
      Object.values(AiChatToolsDependency)
    ),

    // redux provided
    scriptId: PropTypes.number.isRequired,
    scriptName: PropTypes.string.isRequired,
    scriptPath: PropTypes.string.isRequired,
    viewAs: PropTypes.oneOf(Object.values(ViewType)).isRequired,
    hiddenLessonState: PropTypes.object,
    selectedSectionId: PropTypes.number,
    userId: PropTypes.number,
    userType: PropTypes.string,
  };

  constructor(props) {
    super(props);
    const showRedirectDialog =
      props.redirectScriptUrl && props.redirectScriptUrl.length > 0;
    this.state = {showRedirectDialog};

    if (props.userType === 'teacher') {
      analyticsReporter.sendEvent(
        EVENTS.UNIT_OVERVIEW_PAGE_VISITED_BY_TEACHER_EVENT,
        {
          'unit name': props.scriptName,
        }
      );
    } else if (props.userType === 'student') {
      analyticsReporter.sendEvent(
        EVENTS.UNIT_OVERVIEW_PAGE_VISITED_BY_STUDENT_EVENT,
        {
          'unit name': props.scriptName,
        }
      );
    } else {
      analyticsReporter.sendEvent(
        EVENTS.UNIT_OVERVIEW_PAGE_VISITED_BY_SIGNED_OUT_USER_EVENT,
        {
          'unit name': props.scriptName,
        }
      );
    }
  }

  onCloseRedirectDialog = () => {
    const {courseName, scriptName} = this.props;
    // Use course name if available, and script name if not.
    onDismissRedirectDialog(courseName || scriptName);
    this.setState({
      showRedirectDialog: false,
    });
  };

  render() {
    const {
      excludeCsfColumnInLegend,
      teacherResources,
      studentResources,
      scriptId,
      scriptName,
      scriptPath,
      viewAs,
      showCourseUnitVersionWarning,
      showScriptVersionWarning,
      showRedirectWarning,
      redirectScriptUrl,
      versions,
      hiddenLessonState,
      selectedSectionId,
      courseName,
      showAssignButton,
      userId,
      assignedSectionId,
      showCalendar,
      weeklyInstructionalMinutes,
      unitCalendarLessons,
      unitHasLevels,
      isMigrated,
      scriptOverviewPdfUrl,
      scriptResourcesPdfUrl,
      isCsdOrCsp,
      completedLessonNumber,
      courseOfferingId,
      courseVersionId,
      courseId,
      isProfessionalLearningCourse,
      isSingleUnitCourse,
      publishedState,
      participantAudience,
      showAiAssessmentsAnnouncement,
      isOnTeacherDashboard = false,
      aiChatToolsDependency,
    } = this.props;

    const displayRedirectDialog =
      redirectScriptUrl && !dismissedRedirectDialog(courseName || scriptName);

    const isHiddenUnit =
      !!selectedSectionId &&
      !!scriptId &&
      isScriptHiddenForSection(hiddenLessonState, selectedSectionId, scriptId);

    return (
      <div>
        {completedLessonNumber && (
          <EndOfLessonDialog lessonNumber={completedLessonNumber} />
        )}
        <div>
          {!this.props.isSingleUnitCourse && this.props.courseLink && (
            <div className={classNames('unit-breadcrumb', styles.navArea)}>
              <a href={this.props.courseLink} className={styles.navLink}>
                {`< ${this.props.courseTitle}`}
              </a>
            </div>
          )}
          {displayRedirectDialog && (
            <RedirectDialog
              isOpen={this.state.showRedirectDialog}
              details={i18n.assignedToNewerVersion()}
              handleClose={this.onCloseRedirectDialog}
              redirectUrl={redirectScriptUrl}
              redirectButtonText={i18n.goToAssignedVersion()}
            />
          )}
          <UnitOverviewHeader
            showCourseUnitVersionWarning={showCourseUnitVersionWarning}
            showScriptVersionWarning={showScriptVersionWarning}
            showRedirectWarning={showRedirectWarning}
            showHiddenUnitWarning={isHiddenUnit}
            versions={versions}
            courseName={courseName}
            courseId={courseId}
            userId={userId}
            isOnTeacherDashboard={isOnTeacherDashboard}
          >
            <UnitOverviewActionRow
              courseVersionId={courseVersionId}
              versions={versions}
              viewAs={viewAs}
              showAssignButton={showAssignButton}
              courseOfferingId={courseOfferingId}
              isSingleUnitCourse={isSingleUnitCourse}
              currentCourseId={courseId}
              scriptId={scriptId}
              participantAudience={participantAudience}
              scriptOverviewPdfUrl={scriptOverviewPdfUrl}
              scriptResourcesPdfUrl={scriptResourcesPdfUrl}
              teacherResources={teacherResources}
              isMigrated={isMigrated}
              aiChatToolsDependency={aiChatToolsDependency}
            />
            {viewAs === ViewType.Instructor &&
              aiChatToolsDependency === AiChatToolsDependency.ESSENTIAL && (
                <RequiresAiChatToolsAlert />
              )}
          </UnitOverviewHeader>
          {/* unit-calendar-for-printing has style `display: none` from `style/curriculum/scripts.scss` which is added from the BE */}
          {showCalendar && viewAs === ViewType.Instructor && (
            <div className="unit-calendar-for-printing print-only">
              <UnitCalendarGrid
                lessons={unitCalendarLessons}
                weeklyInstructionalMinutes={weeklyInstructionalMinutes || 225}
                weekWidth={550}
              />
            </div>
          )}
          <UnitOverviewTopRow
            teacherResources={teacherResources}
            studentResources={studentResources}
            assignedSectionId={assignedSectionId}
            showCalendar={showCalendar}
            weeklyInstructionalMinutes={weeklyInstructionalMinutes}
            unitCalendarLessons={unitCalendarLessons}
            isMigrated={isMigrated}
            scriptOverviewPdfUrl={scriptOverviewPdfUrl}
            scriptResourcesPdfUrl={scriptResourcesPdfUrl}
            isProfessionalLearningCourse={isProfessionalLearningCourse}
            courseLink={this.props.courseLink}
            publishedState={publishedState}
            participantAudience={participantAudience}
            isUnitWithLevels={unitHasLevels}
            scriptPath={scriptPath}
          />
        </div>
        <ProgressTable minimal={false} />
        <ProgressLegend
          includeCsfColumn={!excludeCsfColumnInLegend}
          includeReviewStates={isCsdOrCsp}
        />
        <GoogleClassroomAttributionLabel />
        {showAiAssessmentsAnnouncement ? (
          <AssessmentsAnnouncementDialog />
        ) : (
          <div id="uitest-no-ai-assessments-announcement" />
        )}
      </div>
    );
  }
}

export const UnconnectedUnitOverview = UnitOverview;
export default connect((state, ownProps) => ({
  scriptId: state.progress.scriptId,
  scriptName: state.progress.scriptName,
  viewAs: state.viewAs,
  hiddenLessonState: state.hiddenLesson,
  selectedSectionId: state.teacherSections.selectedSectionId,
}))(UnconnectedUnitOverview);
